import {
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Box,
  Typography
} from "@mui/material";
import {
  TreeView,
  TreeItem
} from "@mui/lab";

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from '@mui/icons-material/Delete';
import CropIcon from '@mui/icons-material/Crop';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { isImage } from "./isImage";
import { FileRemoveModal } from "./FileRemoveModal"
import { AdditionalImageEditModal } from "src/components/gallery/AdditionalImageEditModal";
import { toDataURL, formatBytes } from "src/common/UtilFunctions";
import ApiResources from "src/providers/ApiResources";
import MultipartUpload from "src/providers/MultipartUpload";
import { useNotify } from 'react-admin';

export type FilePickerProps = {
  setSelected: (paths: string[] | undefined) => void;
  handleDirectorySelect: any;
  handleDirectoryToggle: any;
  multiple?: boolean;
  selectedDirectory? : string;
  expandedDirectory? : string[];
  selected: any[];
  setLoading: any;
  tree: any[];
  data: any[];
  path: string;
};

export const FilePicker = forwardRef((props: FilePickerProps, _ref) => {
  const { path, tree, data, setSelected, handleDirectorySelect, handleDirectoryToggle, selectedDirectory, setLoading, expandedDirectory, selected, multiple = false } = props;

  const [fileData, setFileData] = useState([]);
  const [dimensions, setDimensions] = useState<any>({});
  const [focusedFile, setFocusedFile] = useState<any>(null);
  const [selectedSize, setSelectedSize] = useState<any>({
    option: 'custom',
    resolution: [100, 100]
  });
  const [editImage, setEditImage] = useState<any>({});
  const [openEditModal, setOpenEditModal] = useState(false);
  const [removeImage, setRemoveImage] = useState<any>({});
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    setFileData(data);
  }, [data]);

  const select = (file) => {
    if (!selected.includes(file)) {
      if (multiple) {
        setSelected([...selected, file]);
      } else {
        setSelected([file]);
      }
    } else {
      setSelected(selected.filter((i) => i.file !== file.file));
    }
  };

  useImperativeHandle(
    _ref,
    () => ({
      getSelectedPaths: () => selected.map((i) => i.file),
    }),
    [selected]
  );

  const getItemName = (item) => item.split("/").pop();
  const isItemSelected = (item) => selected.includes(item);

  const TreeDirectory = (props: TreeDirectoryProps) => {
    const { nodeId, label, children } = props;

    return (
      <React.Fragment>
      { children.length > 0 ?
        <TreeItem nodeId={nodeId} label={label}>
        {children.map((directory) => (
          <TreeDirectory key={directory.pathItem} children={directory.children} nodeId={directory.pathItem} label={directory.name} />
        ))}
        </TreeItem>
        :
        <TreeItem nodeId={nodeId} label={label} />
      }
      </React.Fragment>
    )
  }

  const handleActions = (index = null, data = {}) => {
    setDimensions(data);
    setFocusedFile(index);
  }

  const handleCrop = (image) => {
    toDataURL(`${process.env.REACT_APP_AWS_CLOUDFRONT_URL}${image}`).then(dataUrl=>{
      setEditImage({previewImage:dataUrl, imagePath: image})
      setOpenEditModal(true);
    }).catch(err=>{
      console.log(err);
    });
  }

  const handleDelete = (file) => {
    setRemoveImage(file);
    setOpenRemoveModal(true);
  }

  const handleCloseRemoveDialog = (file) => {
    setRemoveImage({});
    setOpenRemoveModal(false);

    setFileData((data) => data.filter((item) => item.file !== file.file));
  }

  const handleCloseEditDialog = (transformedImage:any) => {
    setOpenEditModal(false);
    setLoading(true);

    var img = new Image();
    img.src = transformedImage;
    img.onload = () => {
      var canvas = document.createElement("canvas");
      var imgwidth = img.width;
      var imgheight = img.height;
      canvas.width = selectedSize.resolution[0];
      canvas.height = selectedSize.resolution[1];
      var ctx = canvas.getContext("2d");
      ctx.drawImage(
        img, 0, 0, imgwidth, imgheight, 0, 0, canvas.width, canvas.height
      );
      canvas.toBlob(function(blob){
        let imageSize = {...selectedSize}
        imageSize.name = imageSize.name+" ("+imageSize.resolution[0]+"x"+imageSize.resolution[1]+")"
        
        let originalFilename = editImage.imagePath.replace(/^.*[\\\/]/, '').replace(/\.[^/.]+$/, '');
        let originalFileExtension = editImage.imagePath.substring(editImage.imagePath.lastIndexOf('.'));


        let fileSize = imageSize.resolution[0]+"x"+imageSize.resolution[1]
        let fileName = originalFilename+"_"+fileSize+originalFileExtension

        const file = new File([blob], fileName, {
          type: blob.type,
        });

        const data = new FormData();
        data.append("files[]", file);
        data.append("path", selectedDirectory); 
        setLoading(true);

        MultipartUpload(
          ApiResources.postImage(path),
          data
        ).then(response => {
          return response.json();
        }).then(response => {
          setLoading(false);
          setFileData([...fileData, response[0]]);
          select(response[0]);
        }).catch(error => {
          setLoading(false);
          notify(error.message, { type: 'warning' });
        });
      },'image/png');
    }
  }


  return (
    <>
    <Box height="450px" sx={{ display: "flex", columnGap: "15px"}}>
      { tree.length > 0 &&
      <Box sx={{ display: "flex", overflowX: "hidden"}}>
        <TreeView
          aria-label="file system navigator"
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          sx={{ minWidth: "250px", flexGrow: 1, overflow: "hidden", overflowY: "auto"}}
          onNodeSelect={handleDirectorySelect}
          onNodeToggle={handleDirectoryToggle}
          expanded={expandedDirectory}
          selected={selectedDirectory}
        >
          {tree.map((directory) => (
            <TreeDirectory key={directory.pathItem} children={directory.children} nodeId={directory.pathItem} label={directory.name} />
          ))}
        </TreeView>
      </Box>
      }
      <Box sx={{ display: "flex", overflowX: "hidden", columnGap: "10px", flexDirection: "column"}}>
        { fileData.length > 0 ?
        <ImageList sx={{ width: 500, marginTop: 0 }} variant="masonry"  cols={2} gap={8}>
          {fileData.map((item, index) => {
            return (
              <ImageListItem
                sx={{ cursor: "pointer", minHeight: "120px", display: "flex", alignItems: "center", background: "#f2f2f2" }}
                key={index}
                className={!isImage(item.file) ? "documentItem" : "imageItem"}
                onMouseLeave={() => handleActions()}
              >
                { isImage(item.file) && 
                <img
                  style={{ 
                    objectFit: "contain",
                    minHeight: "120px",
                  }}
                  src={`${process.env.REACT_APP_AWS_CLOUDFRONT_URL}${item.file}`}
                  loading="lazy"
                  onClick={() => select(item)}
                  onMouseEnter={(image) => handleActions(index, {w: image.currentTarget.naturalWidth, h: image.currentTarget.naturalHeight })}
                />
                }

                { !isImage(item.file) && 
                <div
                  style={{ 
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "120px",
                    width: "100%"
                  }}
                  onClick={() => select(item)}
                  onMouseEnter={() => handleActions(index)}
                >
                  <Typography 
                    sx={{
                      fontSize: "24px",
                      fontWeight: "600",
                      fontStyle: "italic",
                      color: "#dddddd"
                    }}
                  >
                    {`${/[^.]+$/.exec(item.file)[0].toUpperCase()} File`}
                  </Typography>
                </div>
                }

                <ImageListItemBar
                  title={getItemName(item.file)}
                  position="top"
                  sx={{
                    background:
                      "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, " +
                      "rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
                  }}
                  onClick={() => select(item)}
                  actionIcon={
                    <IconButton sx={{ color: "white" }}>
                      {isItemSelected(item) ? (
                        <CheckBoxOutlinedIcon />
                      ) : (
                        <CheckBoxOutlineBlankOutlinedIcon />
                      )}
                    </IconButton>
                  }
                />

                { isImage(item.file) && focusedFile == index &&
                <ImageListItemBar
                  title={dimensions && isImage(item.file) ? `${dimensions.w}x${dimensions.h}` : null}
                  subtitle={formatBytes(item.size)}
                  position="bottom"
                  sx={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, " +
                      "rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
                  }}
                  actionIcon={
                    <>
                    <IconButton sx={{ color: "white" }} onClick={() => handleCrop(item.file)}>
                      <CropIcon />
                    </IconButton>
                    <IconButton sx={{ color: "white" }} onClick={() => handleDelete(item)}>
                      <DeleteIcon />
                    </IconButton>
                    </>
                  }
                />
                }

                { !isImage(item.file) && focusedFile == index &&
                <ImageListItemBar
                  title={formatBytes(item.size)}
                  position="bottom"
                  sx={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, " +
                      "rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
                  }}
                  actionIcon={
                    <>
                    <IconButton sx={{ color: "white" }} onClick={() => handleDelete(item)}>
                      <DeleteIcon />
                    </IconButton>
                    </>
                  }
                />
                }
              </ImageListItem>
            )
          }
        )}
        </ImageList>
        :
          <Typography sx={{ textAlign: "center", width: 500, marginTop: 0 }}>There are no items in this directory.</Typography>
        }
      </Box>
    </Box>

    <AdditionalImageEditModal 
      image={editImage} 
      imageSize={selectedSize} 
      open={openEditModal} 
      setOpen={setOpenEditModal} 
      onClose={handleCloseEditDialog}
    />

    <FileRemoveModal
      open={openRemoveModal} 
      setOpen={setOpenRemoveModal}
      onClose={handleCloseRemoveDialog}
      file={removeImage}
    />
    </>
  );
});

export interface TreeDirectoryProps {
  nodeId: string,
  label: string,
  children: any[]
}